/**
 * Tasks strings (`tasks.*`), merged into the dictionary through `extra.ts`.
 * `tasksEn` is typed against `tasksPl`, so a missing key is a compile error.
 */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const tasksPl = {
  subtitle: 'Zadania handlowe i automatyczne kolejne kontakty.',
  add: 'Nowe zadanie',
  auto: 'AUTO',
  autoHint: 'Zadanie utworzone automatycznie przez system.',
  prio: { HIGH: 'Ważne', URGENT: 'Pilne' },
  repeats: { DAILY: 'Codziennie', WEEKLY: 'Co tydzień', BIWEEKLY: 'Co 2 tygodnie', MONTHLY: 'Co miesiąc' },
  reminderHint: 'Przypomnienie włączone',
  tabs: { today: 'Dziś', overdue: 'Zaległe', upcoming: 'Nadchodzące', done: 'Zrobione' },
  filters: {
    type: 'Typ zadania',
    allTypes: 'Wszystkie typy',
    assignee: 'Odpowiedzialny',
    allAssignees: 'Wszyscy',
    customer: 'Klient',
    customerSearch: 'Filtruj po kliencie…',
  },
  type: {
    CALL: 'Telefon',
    VISIT: 'Wizyta',
    EMAIL: 'E-mail',
    FOLLOW_UP: 'Kontakt',
    OFFER: 'Oferta',
    PAYMENT_REMINDER: 'Płatność',
    OTHER: 'Inne',
  },
  status: { OPEN: 'Otwarte', IN_PROGRESS: 'W toku', DONE: 'Zrobione', CANCELLED: 'Anulowane' },
  empty: {
    today: 'Brak zadań na dziś',
    overdue: 'Brak zaległych zadań',
    upcoming: 'Brak nadchodzących zadań',
    done: 'Brak zrobionych zadań',
    body: 'Zmień filtry lub dodaj nowe zadanie.',
    overdueBody: 'Wszystko jest na bieżąco.',
  },
  loadError: 'Nie udało się wczytać zadań.',
  row: {
    noDue: 'bez terminu',
    overdue: 'zaległe',
    doneAt: 'zrobione {when}',
    complete: 'Zakończ zadanie',
    edit: 'Edytuj',
    lead: 'Lead',
  },
  dialog: {
    createTitle: 'Nowe zadanie',
    editTitle: 'Edytuj zadanie',
    ideas: 'Podpowiedzi',
    alertIdeas: 'Z alertów klienta',
    alertTpl: {
      SALES_DROP: 'Omówić spadek sprzedaży z klientem',
      NO_ORDER: 'Zadzwonić — dawno bez zamówienia',
      MARGIN_DROP: 'Przejrzeć cennik — spadek marży',
      PRODUCT_LOST: 'Zapytać o utracony produkt',
      CROSS_SELL: 'Zaproponować produkty uzupełniające',
      VISIT_DUE: 'Umówić zaległą wizytę',
      DELIVERY_FAILED: 'Skontaktować się z klientem po nieudanej dostawie',
      DELIVERY_LATE: 'Sprawdzić spóźnioną dostawę z klientem',
    },
    tpl: {
      CALL: 'Zadzwonić w sprawie oferty|Potwierdzić zamówienie|Zapytać o zadowolenie z dostaw',
      VISIT: 'Wizyta handlowa|Prezentacja nowych produktów|Omówić warunki współpracy',
      EMAIL: 'Wysłać ofertę e-mailem|Przesłać aktualny cennik|Podziękować za zamówienie',
      FOLLOW_UP: 'Ponowny kontakt po ofercie|Sprawdzić decyzję klienta|Wrócić do rozmowy',
      OFFER: 'Przygotować ofertę|Zaktualizować cennik klienta|Wycena nowego asortymentu',
      PAYMENT_REMINDER: 'Przypomnieć o zaległej fakturze|Potwierdzić termin płatności|Uzgodnić plan spłaty',
      OTHER: 'Uzupełnić dane klienta|Zebrać opinię o produktach|Sprawdzić reklamację',
    },
    presetToday: 'Dziś',
    presetTomorrow: 'Jutro',
    presetMonday: 'Poniedziałek',
    presetWeek: 'Za tydzień',
    presetNone: 'Bez terminu',
    pickDate: 'Inna data',
    time: 'Godzina',
    allDay: 'Cały dzień',
    otherTime: 'Inna',
    noDue: 'bez terminu',
    needsDue: 'Ustaw termin, aby włączyć przypomnienie i powtarzanie.',
    repeatHint: 'Po zakończeniu zadania pojawi się kolejne.',
    assignHint: 'Osoba dostanie powiadomienie.',
    linkCustomer: 'Klient',
    linkLead: 'Lead',
    leadPlaceholder: 'Szukaj leada…',
    leadNone: 'Brak leadów dla „{q}”',
    leadClear: 'Usuń wybór leada',
    sectionWhen: 'Kiedy',
    sectionHow: 'Ważność i przypomnienia',
    sectionWho: 'Kogo dotyczy',
    priority: 'Priorytet',
    priorityOpt: { NORMAL: 'Zwykłe', HIGH: 'Ważne', URGENT: 'Pilne' },
    reminder: 'Przypomnienie',
    reminderOpt: { none: 'Brak', r0: 'W chwili terminu', r15: '15 min przed', r60: '1 godz. przed', r1440: 'Dzień wcześniej' },
    reminderNeedsDue: 'Wymaga terminu',
    repeat: 'Powtarzaj',
    repeatOpt: { NONE: 'Nie powtarza', DAILY: 'Codziennie', WEEKLY: 'Co tydzień', BIWEEKLY: 'Co 2 tygodnie', MONTHLY: 'Co miesiąc' },
    type: 'Typ',
    title: 'Tytuł',
    titlePlaceholder: 'np. Zadzwonić w sprawie oferty',
    description: 'Opis',
    due: 'Termin',
    customer: 'Klient',
    customerPlaceholder: 'Szukaj klienta po nazwie lub NIP…',
    customerNone: 'Nie znaleziono klientów',
    customerSearching: 'Szukam…',
    customerTyping: 'Wpisz co najmniej 2 znaki',
    customerClear: 'Usuń wybór klienta',
    assignee: 'Odpowiedzialny',
    me: 'Ja',
    titleRequired: 'Podaj tytuł zadania.',
    created: 'Dodano zadanie',
    updated: 'Zapisano zmiany',
    create: 'Dodaj zadanie',
    save: 'Zapisz',
    cancel: 'Anuluj',
  },
  complete: {
    title: 'Zakończ zadanie',
    question: 'Klient zainteresowany? Zaplanuj ponowny kontakt.',
    in7: 'Kontakt za 7 dni',
    in14: 'Kontakt za 14 dni',
    in30: 'Kontakt za 30 dni',
    none: 'Bez kolejnego kontaktu',
    nextType: 'Typ kolejnego zadania',
    notes: 'Notatka',
    notesPlaceholder: 'Krótka notatka z rozmowy (opcjonalnie)',
    submit: 'Zakończ',
    done: 'Zadanie zakończone',
    nextCreated: 'Utworzono kolejne zadanie: {title} ({when})',
    nextCreatedShort: 'Utworzono kolejne zadanie ({when})',
  },
  view: {
    quickAdd: 'Szybko dodaj zadanie na jutro — wpisz tytuł i naciśnij Enter',
    quickMore: 'Więcej opcji',
    quickAdded: 'Dodano zadanie na jutro, 9:00',
    views: { list: 'Lista', board: 'Tablica', week: 'Tydzień', team: 'Zespół' },
    prio: { ALL: 'Wszystkie', HIGH: 'Ważne', URGENT: 'Pilne' },
    search: 'Szukaj w zadaniach…',
    shortcut: 'N — nowe zadanie',
    groups: {
      overdue: 'Zaległe',
      today: 'Dziś',
      tomorrow: 'Jutro',
      week: 'W tym tygodniu',
      later: 'Później',
      none: 'Bez terminu',
      doneToday: 'Zrobione dziś',
      doneYesterday: 'Wczoraj',
      doneEarlier: 'Wcześniej',
    },
    snooze: { label: 'Przełóż', tomorrow: 'Na jutro', monday: 'Na poniedziałek', week: 'Za tydzień', done: 'Przełożono: {when}' },
    boardEmpty: 'Brak zadań',
    boardNote: 'Tablica pokazuje otwarte zadania (do 100) pogrupowane według terminu.',
    bulk: {
      selected: 'Zaznaczono: {count}',
      complete: 'Zakończ',
      snooze: 'Przełóż',
      assign: 'Przypisz',
      priority: 'Priorytet',
      clear: 'Odznacz',
      selectGroup: 'Zaznacz sekcję',
      selectRow: 'Zaznacz zadanie',
      done: 'Gotowe: {count}',
      partial: 'Gotowe: {done}, nie udało się: {failed}',
      failed: 'Nie udało się wykonać akcji',
      pickDay: 'Inny dzień',
    },
    inline: { editTitle: 'Kliknij dwukrotnie, aby zmienić tytuł', changeDue: 'Zmień termin', saved: 'Zapisano' },
    saved: {
      title: 'Widoki',
      save: 'Zapisz bieżący widok',
      namePlaceholder: 'Nazwa widoku',
      add: 'Zapisz',
      remove: 'Usuń widok',
      mineUrgent: 'Moje pilne',
      todayMine: 'Dziś u mnie',
      overdueTeam: 'Zaległe zespołu',
      importantBoard: 'Ważne — tablica',
    },
    week: {
      prev: 'Poprzedni tydzień',
      next: 'Następny tydzień',
      current: 'Ten tydzień',
      overdue: 'Zaległe',
      overdueHint: 'Przeciągnij na dzień',
      drop: 'Upuść tutaj',
      moved: 'Przeniesiono na {when}',
      note: 'Przeciągnij zadanie na inny dzień, aby zmienić termin (godzina zostaje).',
      empty: 'Brak zadań',
    },
    team: {
      note: 'Obciążenie zespołu zadaniami. Kliknij osobę, aby zobaczyć jej zadania.',
      open: 'Otwarte',
      overdue: 'Zaległe',
      today: 'Dziś',
      important: 'Ważne',
      done7: 'Zrobione (7d)',
      total: 'Razem',
      show: 'Pokaż zadania',
      empty: 'Brak danych o zespole.',
      barLabel: 'Zaległe, dziś i pozostałe otwarte zadania',
    },
  },
};

export type TasksMessages = Shape<typeof tasksPl>;

export const tasksEn: TasksMessages = {
  subtitle: 'Sales tasks and automatic follow-up contacts.',
  add: 'New task',
  auto: 'AUTO',
  autoHint: 'Task created automatically by the system.',
  prio: { HIGH: 'Important', URGENT: 'Urgent' },
  repeats: { DAILY: 'Daily', WEEKLY: 'Weekly', BIWEEKLY: 'Every 2 weeks', MONTHLY: 'Monthly' },
  reminderHint: 'Reminder on',
  tabs: { today: 'Today', overdue: 'Overdue', upcoming: 'Upcoming', done: 'Done' },
  filters: {
    type: 'Task type',
    allTypes: 'All types',
    assignee: 'Assignee',
    allAssignees: 'Everyone',
    customer: 'Customer',
    customerSearch: 'Filter by customer…',
  },
  type: {
    CALL: 'Call',
    VISIT: 'Visit',
    EMAIL: 'E-mail',
    FOLLOW_UP: 'Follow-up',
    OFFER: 'Offer',
    PAYMENT_REMINDER: 'Payment',
    OTHER: 'Other',
  },
  status: { OPEN: 'Open', IN_PROGRESS: 'In progress', DONE: 'Done', CANCELLED: 'Cancelled' },
  empty: {
    today: 'No tasks for today',
    overdue: 'No overdue tasks',
    upcoming: 'No upcoming tasks',
    done: 'No completed tasks',
    body: 'Change the filters or add a new task.',
    overdueBody: 'Everything is on track.',
  },
  loadError: 'Could not load tasks.',
  row: {
    noDue: 'no due date',
    overdue: 'overdue',
    doneAt: 'done {when}',
    complete: 'Complete task',
    edit: 'Edit',
    lead: 'Lead',
  },
  dialog: {
    createTitle: 'New task',
    editTitle: 'Edit task',
    ideas: 'Ideas',
    alertIdeas: 'From customer alerts',
    alertTpl: {
      SALES_DROP: 'Discuss the sales drop with the customer',
      NO_ORDER: 'Call — no order for a while',
      MARGIN_DROP: 'Review the price list — margin drop',
      PRODUCT_LOST: 'Ask about the lost product',
      CROSS_SELL: 'Propose complementary products',
      VISIT_DUE: 'Schedule the overdue visit',
      DELIVERY_FAILED: 'Contact the customer after the failed delivery',
      DELIVERY_LATE: 'Check the late delivery with the customer',
    },
    tpl: {
      CALL: 'Call about the offer|Confirm the order|Ask about delivery satisfaction',
      VISIT: 'Sales visit|Present new products|Discuss terms of cooperation',
      EMAIL: 'Send the offer by e-mail|Send the current price list|Thank them for the order',
      FOLLOW_UP: 'Follow up after the offer|Check the customer’s decision|Pick the conversation back up',
      OFFER: 'Prepare an offer|Update the customer’s price list|Quote for a new assortment',
      PAYMENT_REMINDER: 'Remind about the overdue invoice|Confirm the payment date|Agree a repayment plan',
      OTHER: 'Complete customer data|Collect product feedback|Check a complaint',
    },
    presetToday: 'Today',
    presetTomorrow: 'Tomorrow',
    presetMonday: 'Monday',
    presetWeek: 'In a week',
    presetNone: 'No date',
    pickDate: 'Other date',
    time: 'Time',
    allDay: 'All day',
    otherTime: 'Other',
    noDue: 'no due date',
    needsDue: 'Set a due date to enable the reminder and repeat.',
    repeatHint: 'When you finish the task, the next one appears.',
    assignHint: 'They will get a notification.',
    linkCustomer: 'Customer',
    linkLead: 'Lead',
    leadPlaceholder: 'Search lead…',
    leadNone: 'No leads for “{q}”',
    leadClear: 'Remove the lead',
    sectionWhen: 'When',
    sectionHow: 'Importance and reminders',
    sectionWho: 'Who it concerns',
    priority: 'Priority',
    priorityOpt: { NORMAL: 'Normal', HIGH: 'Important', URGENT: 'Urgent' },
    reminder: 'Reminder',
    reminderOpt: { none: 'None', r0: 'At the due time', r15: '15 min before', r60: '1 hour before', r1440: 'A day before' },
    reminderNeedsDue: 'Needs a due date',
    repeat: 'Repeat',
    repeatOpt: { NONE: 'Does not repeat', DAILY: 'Daily', WEEKLY: 'Weekly', BIWEEKLY: 'Every 2 weeks', MONTHLY: 'Monthly' },
    type: 'Type',
    title: 'Title',
    titlePlaceholder: 'e.g. Call about the offer',
    description: 'Description',
    due: 'Due',
    customer: 'Customer',
    customerPlaceholder: 'Search customer by name or tax ID…',
    customerNone: 'No customers found',
    customerSearching: 'Searching…',
    customerTyping: 'Type at least 2 characters',
    customerClear: 'Remove customer',
    assignee: 'Assignee',
    me: 'Me',
    titleRequired: 'Enter a task title.',
    created: 'Task added',
    updated: 'Changes saved',
    create: 'Add task',
    save: 'Save',
    cancel: 'Cancel',
  },
  complete: {
    title: 'Complete task',
    question: 'Is the customer interested? Plan the next contact.',
    in7: 'Contact in 7 days',
    in14: 'Contact in 14 days',
    in30: 'Contact in 30 days',
    none: 'No follow-up',
    nextType: 'Next task type',
    notes: 'Note',
    notesPlaceholder: 'Short note from the conversation (optional)',
    submit: 'Complete',
    done: 'Task completed',
    nextCreated: 'Next task created: {title} ({when})',
    nextCreatedShort: 'Next task created ({when})',
  },
  view: {
    quickAdd: 'Quickly add a task for tomorrow — type a title and press Enter',
    quickMore: 'More options',
    quickAdded: 'Task added for tomorrow, 9:00',
    views: { list: 'List', board: 'Board', week: 'Week', team: 'Team' },
    prio: { ALL: 'All', HIGH: 'Important', URGENT: 'Urgent' },
    search: 'Search tasks…',
    shortcut: 'N — new task',
    groups: {
      overdue: 'Overdue',
      today: 'Today',
      tomorrow: 'Tomorrow',
      week: 'This week',
      later: 'Later',
      none: 'No due date',
      doneToday: 'Done today',
      doneYesterday: 'Yesterday',
      doneEarlier: 'Earlier',
    },
    snooze: { label: 'Postpone', tomorrow: 'To tomorrow', monday: 'To Monday', week: 'In a week', done: 'Postponed: {when}' },
    boardEmpty: 'No tasks',
    boardNote: 'The board shows open tasks (up to 100) grouped by due date.',
    bulk: {
      selected: 'Selected: {count}',
      complete: 'Complete',
      snooze: 'Postpone',
      assign: 'Assign',
      priority: 'Priority',
      clear: 'Clear',
      selectGroup: 'Select section',
      selectRow: 'Select task',
      done: 'Done: {count}',
      partial: 'Done: {done}, failed: {failed}',
      failed: 'The action failed',
      pickDay: 'Other day',
    },
    inline: { editTitle: 'Double-click to rename', changeDue: 'Change due date', saved: 'Saved' },
    saved: {
      title: 'Views',
      save: 'Save current view',
      namePlaceholder: 'View name',
      add: 'Save',
      remove: 'Remove view',
      mineUrgent: 'My urgent',
      todayMine: 'Mine today',
      overdueTeam: 'Team overdue',
      importantBoard: 'Important — board',
    },
    week: {
      prev: 'Previous week',
      next: 'Next week',
      current: 'This week',
      overdue: 'Overdue',
      overdueHint: 'Drag onto a day',
      drop: 'Drop here',
      moved: 'Moved to {when}',
      note: 'Drag a task onto another day to change its due date (the time stays).',
      empty: 'No tasks',
    },
    team: {
      note: 'Task load of the team. Click a person to see their tasks.',
      open: 'Open',
      overdue: 'Overdue',
      today: 'Today',
      important: 'Important',
      done7: 'Done (7d)',
      total: 'Total',
      show: 'Show tasks',
      empty: 'No team data.',
      barLabel: 'Overdue, today and other open tasks',
    },
  },
};
